<script setup lang="ts">
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useDeleteSector } from "~/modules/sector/composables/useDeleteSector";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  isVisible: boolean;
  sector?: Sector;
}>();
const emit = defineEmits(["change", "close"]);

const { t } = useI18n();

const { retrieveSectors } = useSectorStore();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { isLoading, error, deleteSector } = useDeleteSector(
  currentOrganization.value?.code,
);

const [removeDeniedDialogVisible, toggleRemoveDeniedDialogVisible] =
  useToggle();

/**
 * Remove a sector
 */
async function onRemoveSector() {
  if (props.sector?.code) {
    await deleteSector(props.sector.code);

    if (error.value) {
      const removeDenied =
        error.value.status === HttpStatusCode.Forbidden &&
        error.value.codes?.find(
          (code) => code.code === "sector_delete_forbidden",
        );

      if (removeDenied) {
        emit("close");
        toggleRemoveDeniedDialogVisible(true);
      } else {
        error.value.notify(NotificationKey.removeError);
      }
    } else {
      showNotificationSuccess(t(NotificationKey.removeSuccess));
      await retrieveSectors();
      emit("change");
    }
  }
}
</script>

<template>
  <confirm-modal
    :is-dialog-visible="isVisible"
    :title="t('sectors.modals.remove.title', { sectorName: sector?.name })"
    :description="t('sectors.modals.remove.description')"
    :disabled-confirm="isLoading"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @cancel="emit('close')"
    @confirm="onRemoveSector"
  />
  <sector-remove-denied
    :is-visible="removeDeniedDialogVisible"
    @close="toggleRemoveDeniedDialogVisible(false)"
  />
</template>
